// Pure chat helpers: the inbox (conversations and unread counts), threads
// (messages merged by seq from REST pages and stream frames, optimistic sends
// keyed by client_id), what a thread shows (day separators, sender groups,
// Seen markers) and what can be done there (message actions, mentions,
// typing, mute, blocks, terms).
import type { Schemas, WsServerMessage } from "@10twenty/api";
import { dayKey, dayTitle, timeText } from "../history/history";
import { locationText, type LocationCreate } from "./location";
import { voiceText, type VoiceClip } from "./voice";
import { mediaText, type Media } from "./media";

export type Conversation = Schemas["Conversation"];
export type Message = Schemas["Message"];
export type ChatUser = Schemas["ChatUser"];
export type ChatFrame = Extract<
  WsServerMessage,
  {
    type:
      | "chat_subscribed"
      | "chat_resync"
      | "chat_message"
      | "chat_conversation"
      | "chat_read"
      | "chat_receipt"
      | "chat_typing";
  }
>;
export type ReportReason = Schemas["ReportReason"];

/**
 * A version 4 UUID for a send's `client_id`. `crypto.randomUUID` exists only in a secure
 * context, which the dev server reached over the LAN (`http://192.168.…`) is not.
 */
export function newClientId(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : uuidFromBytes(crypto.getRandomValues(new Uint8Array(16)));
}

/** Formats 16 random bytes as a version 4 UUID. */
export function uuidFromBytes(random: Uint8Array): string {
  const bytes = Uint8Array.from(random);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Longest message body, in code points (as the server counts). */
export const MAX_BODY = 4_000;
/** The composer shows how many characters are left from here on. */
export const BODY_COUNT_FROM = 3_500;
/** Consecutive messages from one sender this close together share a group. */
const GROUP_GAP_MS = 5 * 60_000;

// ------------------------------------------------------------------ text

export function codePoints(text: string): number {
  let n = 0;
  for (const _ of text) n++;
  return n;
}

export interface BodyCheck {
  /** What would be sent (trimmed). */
  body: string;
  length: number;
  ok: boolean;
}

export function checkBody(text: string): BodyCheck {
  const body = text.trim();
  const length = codePoints(body);
  return { body, length, ok: length > 0 && length <= MAX_BODY && !body.includes("\0") };
}

export type Users = Readonly<Record<string, ChatUser>>;

/** Adds `list` to `users`, returning `users` itself when nothing changed. */
export function withUsers(users: Users, list: readonly ChatUser[]): Users {
  let next: Record<string, ChatUser> | null = null;
  for (const user of list) {
    const old = users[user.id];
    if (old && old.name === user.name && old.avatar_small_url === user.avatar_small_url) continue;
    next ??= { ...users };
    next[user.id] = user;
  }
  return next ?? users;
}

/** A person's name as a chat shows it; deleted accounts are absent from `users`. */
export function personName(users: Users, id: string | null | undefined): string {
  return (id && users[id]?.name) || "Deleted account";
}

export function conversationTitle(conversation: Conversation): string {
  return conversation.title ?? (conversation.kind === "direct" ? "Deleted account" : "Chat");
}

/** The text of a system message. */
export function systemText(message: Message, users: Users, selfId: string): string {
  const event = message.system;
  if (!event) return "";
  const who = (id: string | null | undefined) => (id === selfId ? "You" : personName(users, id));
  const subject = who(event.user_id);
  switch (event.event) {
    case "joined":
      return `${subject} joined`;
    case "left":
      return `${subject} left`;
    case "removed":
      if (!event.actor_id) return `${subject} ${subject === "You" ? "were" : "was"} removed`;
      return `${who(event.actor_id)} removed ${subject === "You" ? "you" : subject}`;
    case "renamed":
      return event.actor_id
        ? `${who(event.actor_id)} renamed the circle to “${event.name ?? ""}”`
        : `The circle was renamed to “${event.name ?? ""}”`;
    case "kind_changed": {
      const kind = event.kind ?? "family";
      return event.actor_id ? `${who(event.actor_id)} made this a ${kind} circle` : `This is now a ${kind} circle`;
    }
    case "event_started":
      return "The event has started";
    case "event_ended":
      return "The event has ended. This chat stays open for 7 days";
    case "chat_closed":
      return "This chat is closed";
    case "event_expiring": {
      const when = event.deletes_at
        ? new Date(event.deletes_at).toLocaleString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "numeric",
            minute: "2-digit",
          })
        : "tomorrow";
      return `This event and its chat will be deleted ${event.deletes_at ? `on ${when}` : when}. Save anything you want to keep`;
    }
    case "timer_changed": {
      const length = event.message_timer ? timerLabel(event.message_timer) : null;
      if (!event.actor_id) return length ? `New messages disappear after ${length}` : "Disappearing messages are off";
      return length
        ? `${who(event.actor_id)} set new messages to disappear after ${length}`
        : `${who(event.actor_id)} turned off disappearing messages`;
    }
  }
}

/** What a tombstone says in place of its text. */
export function unsentText(message: Message, users: Users, selfId: string): string {
  if (message.deleted_by && message.deleted_by !== message.sender_id) {
    return `Removed by ${message.deleted_by === selfId ? "you" : personName(users, message.deleted_by)}`;
  }
  return message.sender_id === selfId ? "You unsent a message" : "Message unsent";
}

/** A message in one line: its text, else what a photo, location or voice message holds. */
export function summaryText(message: Pick<Message, "kind" | "body" | "attachments" | "location" | "voice">): string {
  if (message.kind === "media") return mediaText(message);
  if (message.kind === "location") return locationText(message);
  if (message.kind === "voice") return voiceText(message);
  return (message.body ?? "").replace(/\s+/g, " ");
}

/** One line for the inbox: the newest message, with its sender in circle chats. */
export function previewText(conversation: Conversation, users: Users, selfId: string, nowMs = Date.now()): string {
  const message = conversation.last_message;
  if (!message) return "No messages yet";
  if (message.kind === "system") return systemText(message, users, selfId);
  if (isExpired(message, nowMs)) return "Message disappeared";
  if (message.deleted_at) return unsentText(message, users, selfId);
  const body = summaryText(message);
  if (message.sender_id === selfId) return `You: ${body}`;
  return conversation.kind === "direct" ? body : `${personName(users, message.sender_id)}: ${body}`;
}

/** The inbox time: the time today, the weekday within a week, else the date. */
export function inboxTime(iso: string, nowMs: number, locale?: string): string {
  const ms = Date.parse(iso);
  const date = new Date(ms);
  if (dayKey(date) === dayKey(new Date(nowMs))) return timeText(ms, locale);
  if (nowMs - ms < 6 * 86_400_000 && ms <= nowMs) return date.toLocaleDateString(locale, { weekday: "short" });
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === new Date(nowMs).getFullYear() ? {} : { year: "numeric" }),
  });
}

// ------------------------------------------------------------------ inbox

/** Direct conversations without a visible message stay out of the inbox. */
export function isListed(conversation: Conversation): boolean {
  return conversation.kind !== "direct" || conversation.last_message !== null;
}

export function isMuted(conversation: Conversation, nowMs: number): boolean {
  return conversation.muted_until !== null && Date.parse(conversation.muted_until) > nowMs;
}

/** What a conversation adds to the "Chats" total: nothing while muted or set to none, else per its notify level. */
export function badgeCount(conversation: Conversation, nowMs: number): number {
  if (!isListed(conversation) || isMuted(conversation, nowMs)) return 0;
  switch (conversation.notify) {
    case "all":
      return conversation.unread_count;
    case "mentions":
      return conversation.mention_count;
    case "none":
      return 0;
  }
}

export function unreadTotal(conversations: readonly Conversation[], nowMs: number): number {
  return conversations.reduce((sum, c) => sum + badgeCount(c, nowMs), 0);
}

/** The badge on the installed app's icon: the unread total, or none (cleared) at zero. */
export function appBadge(total: number): number | null {
  return total > 0 ? total : null;
}

export interface ChatRead {
  conversation_id: string;
  seq: number;
}

/**
 * Read positions the service worker hasn't been told yet (`posted`, by
 * conversation): it closes notifications up to them and skips older pushes
 * (public/sw.js).
 */
export function newReads(posted: ReadonlyMap<string, number>, conversations: readonly Conversation[]): ChatRead[] {
  return conversations
    .filter((c) => c.read_seq > (posted.get(c.id) ?? 0))
    .map((c) => ({ conversation_id: c.id, seq: c.read_seq }));
}

/** Newest activity first; ties by id so the order is stable. */
export function sortInbox(conversations: Conversation[]): Conversation[] {
  return conversations.sort(
    (a, b) => Date.parse(b.last_activity_at) - Date.parse(a.last_activity_at) || (a.id < b.id ? -1 : 1),
  );
}

export function upsertConversation(list: readonly Conversation[], conversation: Conversation): Conversation[] {
  return sortInbox([...list.filter((c) => c.id !== conversation.id), conversation]);
}

export function removeConversation(list: readonly Conversation[], id: string): Conversation[] {
  return list.filter((c) => c.id !== id);
}

/**
 * Merges a full inbox fetch into the cached list. Conversations frames
 * changed while it was in flight (`touched`) keep the cached copy, which may
 * be newer (the caller refetches them one by one); `removed` ones stay out.
 */
export function mergeInbox(
  cached: readonly Conversation[],
  fresh: readonly Conversation[],
  touched: ReadonlySet<string>,
  removed: ReadonlySet<string>,
): Conversation[] {
  const old = new Map(cached.map((c) => [c.id, c]));
  const merged = new Map<string, Conversation>();
  for (const c of fresh) {
    if (removed.has(c.id)) continue;
    const prev = old.get(c.id);
    merged.set(c.id, prev && touched.has(c.id) ? prev : c);
  }
  for (const id of touched) {
    const prev = old.get(id);
    if (prev && !merged.has(id) && !removed.has(id)) merged.set(id, prev);
  }
  return sortInbox([...merged.values()]);
}

/** Whether a message counts as unread for `selfId` (others' text messages still showing). */
function countsAsUnread(message: Message, selfId: string): boolean {
  return message.kind !== "system" && message.sender_id !== selfId && message.deleted_at === null;
}

/**
 * A conversation after a `chat_message` frame: its preview, head and activity
 * time, and (for a message past the known head) its unread and mention counts.
 */
export function withMessage(conversation: Conversation, message: Message, selfId: string): Conversation {
  conversation = withPinnedMessage(conversation, message);
  const last = conversation.last_message;
  const isNew = message.seq > conversation.head_seq;
  const preview =
    last === null || message.seq > last.seq || (message.seq === last.seq && isCurrent(message, last)) ? message : last;
  if (!isNew && preview === last) return conversation;
  const unread = isNew && message.seq > conversation.read_seq && countsAsUnread(message, selfId);
  return {
    ...conversation,
    last_message: preview,
    head_seq: Math.max(conversation.head_seq, message.seq),
    last_activity_at:
      isNew && Date.parse(message.created_at) > Date.parse(conversation.last_activity_at)
        ? message.created_at
        : conversation.last_activity_at,
    unread_count: conversation.unread_count + (unread ? 1 : 0),
    mention_count: conversation.mention_count + (unread && message.mentions.includes(selfId) ? 1 : 0),
  };
}

/**
 * Keeps the pinned message's copy current: a newer version of it (an ETA
 * update, a location payload removed) replaces it, and unsending it unpins.
 */
export function withPinnedMessage(conversation: Conversation, message: Message): Conversation {
  const pinned = conversation.pinned;
  if (!pinned || pinned.message.id !== message.id || !isCurrent(message, pinned.message)) return conversation;
  if (message.deleted_at !== null) return { ...conversation, pinned: null };
  return { ...conversation, pinned: { ...pinned, message } };
}

/** The user's own read position moved; positions never go back. */
export function withRead(
  conversation: Conversation,
  read: { read_seq: number; unread_count: number; mention_count: number },
): Conversation {
  if (read.read_seq < conversation.read_seq) return conversation;
  return {
    ...conversation,
    read_seq: read.read_seq,
    unread_count: read.unread_count,
    mention_count: read.mention_count,
  };
}

// ----------------------------------------------------------------- threads

export interface PendingSend {
  client_id: string;
  /** Empty for a media message without a caption. */
  body: string;
  /** A media message's uploads, shown while it is sent. */
  attachments?: Media[];
  /** When it was written, for ordering and grouping (local clock). */
  created_at: string;
  status: "sending" | "failed";
  /** Why the last attempt failed. */
  error: string | null;
  /** The server's problem code, when it gave one (`terms_required`, `not_writable`). */
  code: string | null;
  /** The message it quotes. */
  reply_to_id?: string | null;
  /** People it mentions. */
  mentions?: string[];
  /** A location message's payload. */
  location?: LocationCreate;
  /** Pin it once sent (a meeting point). */
  pin?: boolean;
  /** A voice message's clip (its upload is the one attachment). */
  voice?: VoiceClip;
}

export interface Thread {
  /** Ascending seq, without gaps once repaired. */
  messages: Message[];
  /** Whether older messages remain to page in (`before`). */
  hasOlder: boolean;
  /** The newest page has loaded. */
  loaded: boolean;
  pending: PendingSend[];
  /** Other participants' read positions, for Seen markers. */
  readPositions: Readonly<Record<string, number>>;
}

export function emptyThread(): Thread {
  return { messages: [], hasOlder: false, loaded: false, pending: [], readPositions: {} };
}

/**
 * Orders two timestamps of one message: ISO strings from the server may carry
 * microseconds, which `Date.parse` drops.
 */
function compareInstants(a: string, b: string): number {
  const diff = Date.parse(a) - Date.parse(b);
  if (diff !== 0 || Number.isNaN(diff)) return diff || 0;
  const fraction = (s: string) => (/\.(\d+)/.exec(s)?.[1] ?? "").padEnd(9, "0");
  const fa = fraction(a);
  const fb = fraction(b);
  return fa < fb ? -1 : fa > fb ? 1 : 0;
}

/** Whether `next` is at least as current as `prev`, the same message. */
function isCurrent(next: Message, prev: Message): boolean {
  return compareInstants(next.updated_at, prev.updated_at) >= 0;
}

/**
 * Merges messages from a page or frame into an ascending list. A message
 * replaces the cached one with its seq only when it is newer (`updated_at`),
 * so a late page never undoes a frame. Returns `existing` when nothing changed.
 */
export function mergeMessages(existing: readonly Message[], incoming: readonly Message[]): Message[] {
  const bySeq = new Map(existing.map((m) => [m.seq, m]));
  let changed = false;
  for (const message of incoming) {
    const prev = bySeq.get(message.seq);
    if (prev && (prev === message || compareInstants(message.updated_at, prev.updated_at) <= 0)) continue;
    bySeq.set(message.seq, message);
    changed = true;
  }
  if (!changed) return existing as Message[];
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/** Drops optimistic sends that have arrived as messages (matched by `client_id`). */
export function settlePending(pending: readonly PendingSend[], messages: readonly Message[]): PendingSend[] {
  if (pending.length === 0) return pending as PendingSend[];
  const arrived = new Set(messages.flatMap((m) => (m.client_id === null ? [] : [m.client_id])));
  const left = pending.filter((p) => !arrived.has(p.client_id));
  return left.length === pending.length ? (pending as PendingSend[]) : left;
}

/**
 * Messages fetched again for freshly signed media URLs: each replaces the
 * cached copy unless that one is newer (a frame changed it meanwhile).
 */
export function withResigned<T extends Thread>(thread: T, items: readonly Message[]): T {
  const bySeq = new Map(items.map((m) => [m.seq, m]));
  let changed = false;
  const messages = thread.messages.map((prev) => {
    const next = bySeq.get(prev.seq);
    if (!next || next.id !== prev.id || compareInstants(next.updated_at, prev.updated_at) < 0) return prev;
    changed = true;
    return next;
  });
  return changed ? { ...thread, messages } : thread;
}

/**
 * The queued seqs a page fetched for fresh URLs (`before` = `newest` + 1)
 * settled: those from its oldest message up to `newest`, and every older one
 * when nothing older is left. Always includes `newest`, so a queue drains.
 */
export function resignCovered(
  queue: Iterable<number>,
  newest: number,
  page: { items: readonly { seq: number }[]; has_more: boolean },
): number[] {
  const oldest = page.has_more ? page.items.reduce((min, m) => Math.min(min, m.seq), newest) : -Infinity;
  return [...queue].filter((seq) => seq <= newest && seq >= oldest);
}

/** Adds messages from a page or frame; `hasOlder` when the page was an older (or the newest) one. */
export function withMessages<T extends Thread>(thread: T, items: readonly Message[], hasOlder?: boolean): T {
  const messages = mergeMessages(thread.messages, items);
  const pending = settlePending(thread.pending, items);
  if (messages === thread.messages && pending === thread.pending && hasOlder === undefined) return thread;
  return { ...thread, messages, pending, ...(hasOlder === undefined ? {} : { hasOlder }) };
}

/**
 * The newest page. With `reset` (the thread was cleared or reloaded) it
 * replaces the cached messages: the page plus `arrived`, the message frames
 * applied while it was in flight (newer than its snapshot; the newer copy of
 * each message wins).
 */
export function withNewestPage<T extends Thread>(
  thread: T,
  items: readonly Message[],
  hasMore: boolean,
  reset = false,
  arrived: readonly Message[] = [],
): T {
  const base = reset ? { ...thread, messages: mergeMessages([], arrived) } : thread;
  return {
    ...withMessages(base, items),
    hasOlder: thread.loaded && !reset ? thread.hasOlder : hasMore,
    loaded: true,
  };
}

export function withReadPosition<T extends Thread>(thread: T, userId: string, readSeq: number): T {
  if ((thread.readPositions[userId] ?? -1) >= readSeq) return thread;
  return { ...thread, readPositions: { ...thread.readPositions, [userId]: readSeq } };
}

export function newestSeq(thread: Thread): number {
  return thread.messages.at(-1)?.seq ?? 0;
}

// ----------------------------------------------------------- thread items

export type ThreadItem =
  | { kind: "day"; key: string; title: string }
  | { kind: "system"; key: string; message: Message }
  | {
      kind: "message";
      key: string;
      message: Message;
      own: boolean;
      /** First and last of a run from one sender (names and avatars go on the first, the time on the last). */
      first: boolean;
      last: boolean;
      /** Others who have read it (only on the caller's newest own message). */
      seenBy: string[];
    }
  | { kind: "pending"; key: string; pending: PendingSend; first: boolean; last: boolean };

interface Entry {
  key: string;
  at: number;
  /** Who sent it; null for system messages (never grouped). */
  sender: string | null;
  item: ThreadItem;
}

/**
 * Who has read the caller's newest own message, as `{seq, userIds}`: "Seen"
 * answers whether they saw what you sent, so it goes under that message only.
 * Null when you have sent nothing loaded or nobody has read it yet.
 */
export function seenMarker(
  messages: readonly Message[],
  readPositions: Readonly<Record<string, number>>,
  selfId: string,
): { seq: number; userIds: string[] } | null {
  const own = messages.findLast((m) => m.sender_id === selfId && m.kind !== "system");
  if (!own) return null;
  const userIds = Object.entries(readPositions)
    .filter(([userId, readSeq]) => userId !== selfId && readSeq >= own.seq)
    .map(([userId]) => userId);
  return userIds.length > 0 ? { seq: own.seq, userIds } : null;
}

/**
 * The rows of a thread: day separators, system lines and messages grouped by
 * sender (same sender, same day, at most five minutes apart), then pending
 * sends after everything that has arrived.
 */
export function threadItems(thread: Thread, selfId: string, nowMs: number, locale?: string): ThreadItem[] {
  const seen = seenMarker(thread.messages, thread.readPositions, selfId);
  const entries: Entry[] = [
    // Disappearing messages go at their expiry, before the server's sweep tombstones them.
    ...thread.messages
      .filter((message) => !isExpired(message, nowMs))
      .map((message): Entry => {
        const at = Date.parse(message.created_at);
        if (message.kind === "system") {
          return { key: message.id, at, sender: null, item: { kind: "system", key: message.id, message } };
        }
        return {
          key: message.id,
          at,
          sender: message.sender_id,
          item: {
            kind: "message",
            key: message.id,
            message,
            own: message.sender_id === selfId,
            first: true,
            last: true,
            seenBy: seen?.seq === message.seq ? seen.userIds : [],
          },
        };
      }),
    ...thread.pending.map((pending): Entry => ({
      key: `pending:${pending.client_id}`,
      at: Date.parse(pending.created_at),
      sender: selfId,
      item: { kind: "pending", key: `pending:${pending.client_id}`, pending, first: true, last: true },
    })),
  ];

  const items: ThreadItem[] = [];
  let day: string | null = null;
  let prev: Entry | null = null;
  for (const entry of entries) {
    const key = dayKey(new Date(entry.at));
    const newDay = key !== day;
    if (newDay) {
      day = key;
      items.push({ kind: "day", key: `day:${key}`, title: dayTitle(key, nowMs, locale) });
    }
    const grouped =
      !newDay && entry.sender !== null && prev?.sender === entry.sender && entry.at - prev.at <= GROUP_GAP_MS;
    const before = items.at(-1);
    if (grouped && (before?.kind === "message" || before?.kind === "pending")) {
      items[items.length - 1] = { ...before, last: false };
    }
    const item = entry.item;
    items.push(item.kind === "message" || item.kind === "pending" ? { ...item, first: !grouped } : item);
    prev = entry;
  }
  return items;
}

/** "Seen" in a DM; else "Seen by Alice, Bob and 2 others". */
export function seenText(userIds: readonly string[], users: Users, direct: boolean): string {
  if (userIds.length === 0) return "";
  if (direct) return "Seen";
  const names = userIds.map((id) => personName(users, id));
  if (names.length <= 3) {
    return `Seen by ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`}`;
  }
  return `Seen by ${names.slice(0, 2).join(", ")} and ${names.length - 2} others`;
}

// --------------------------------------------------------------- actions

/** Own text messages can be edited this long after sending (the server's window). */
export const EDIT_WINDOW_MS = 15 * 60_000;

export interface MessageActions {
  react: boolean;
  reply: boolean;
  edit: boolean;
  /** Take back your own message (also in a read-only chat). */
  unsend: boolean;
  /** A circle admin removes someone else's message. */
  remove: boolean;
  report: boolean;
  block: boolean;
  /** Pin it for everyone (replacing the pinned message). */
  pin: boolean;
  /** It is the pinned message: unpin it (also in a read-only chat). */
  unpin: boolean;
}

/** What the caller may do with a message; `admin` is their role in the conversation's circle. */
export function messageActions(
  message: Message,
  selfId: string,
  context: { writable: boolean; admin: boolean; nowMs: number; pinnedId?: string | null },
): MessageActions {
  const live =
    (message.kind === "text" || message.kind === "media" || message.kind === "location" || message.kind === "voice") &&
    message.deleted_at === null;
  const pinned = context.pinnedId === message.id;
  const own = message.sender_id === selfId;
  // Someone else's, including a deleted account's (null sender): reportable and removable, not blockable.
  const other = live && !own;
  return {
    react: live && context.writable,
    reply: live && context.writable,
    // A media message's caption is editable once it has one.
    edit:
      live &&
      own &&
      message.body !== null &&
      context.writable &&
      context.nowMs - Date.parse(message.created_at) < EDIT_WINDOW_MS,
    unsend: live && own,
    remove: other && context.admin,
    report: other,
    block: other && message.sender_id !== null,
    pin: live && context.writable && !pinned,
    unpin: live && pinned,
  };
}

/** Reactions offered in one tap; the picker has the rest. */
export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"] as const;

/** The emoji picker's groups (each a single RGI emoji, as the server requires). */
export const EMOJI_GROUPS: readonly { name: string; emoji: readonly string[] }[] = [
  {
    name: "Smileys",
    emoji: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😆",
      "😅",
      "🤣",
      "😂",
      "🙂",
      "😉",
      "😊",
      "😇",
      "🥰",
      "😍",
      "🤩",
      "😘",
      "😋",
      "😛",
      "😜",
      "🤪",
      "🤗",
      "🤭",
      "🤫",
      "🤔",
      "😐",
      "😑",
      "😶",
      "🙄",
      "😏",
      "😬",
      "😌",
      "😔",
      "😴",
      "🤒",
      "🤧",
      "🥵",
      "🥶",
      "🥴",
      "😵",
      "🤯",
      "🥳",
      "😎",
      "🤓",
      "😕",
      "😟",
      "😮",
      "😲",
      "😳",
      "🥺",
      "😢",
      "😭",
      "😱",
      "😖",
      "😞",
      "😩",
      "😫",
      "😤",
      "😡",
      "🤬",
      "💀",
      "🤡",
      "👻",
      "👽",
      "🤖",
    ],
  },
  {
    name: "People",
    emoji: [
      "👍",
      "👎",
      "👌",
      "✌️",
      "🤞",
      "🤟",
      "🤙",
      "👋",
      "👏",
      "🙌",
      "👐",
      "🤲",
      "🤝",
      "🙏",
      "💪",
      "👀",
      "🫶",
      "🙋",
      "🤷",
      "🤦",
      "🙇",
      "💃",
      "🕺",
      "🏃",
      "🚶",
      "🧘",
    ],
  },
  {
    name: "Hearts",
    emoji: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "❣️", "💕", "💖", "💯", "✨", "🔥", "⭐"],
  },
  {
    name: "Things",
    emoji: [
      "🎉",
      "🎂",
      "🎁",
      "🏠",
      "🏫",
      "🏢",
      "🚗",
      "🚌",
      "🚲",
      "✈️",
      "⛽",
      "📍",
      "🗺️",
      "⏰",
      "📱",
      "🔋",
      "☕",
      "🍕",
      "🍔",
      "🍦",
      "🍎",
      "🛒",
      "⚽",
      "🏀",
      "🎮",
      "🎵",
      "📚",
      "💤",
      "☀️",
      "🌧️",
      "❄️",
      "🌈",
      "🐶",
      "🐱",
      "✅",
      "❌",
      "❓",
      "❗",
      "⚠️",
      "🆗",
    ],
  },
];

export function myReaction(message: Message, selfId: string): string | null {
  return message.reactions.find((r) => r.user_ids.includes(selfId))?.emoji ?? null;
}

/** Tapping an emoji sets it as your reaction, or takes it back when it already is (null). */
export function reactionAfterTap(message: Message, selfId: string, emoji: string): string | null {
  return myReaction(message, selfId) === emoji ? null : emoji;
}

/** A short single-line excerpt, for the reply and edit banners. */
export function excerpt(text: string | null | undefined, max = 80): string {
  const line = (text ?? "").replace(/\s+/g, " ").trim();
  const chars = [...line];
  return chars.length <= max ? line : `${chars.slice(0, max - 1).join("")}…`;
}

// -------------------------------------------------------------- mentions

export interface MentionQuery {
  /** Where the `@` is. */
  start: number;
  /** What follows it up to the caret. */
  query: string;
}

/** The `@name` being typed at `caret`, if any: an `@` that starts a word, then at most a few words. */
export function mentionQuery(text: string, caret: number): MentionQuery | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf("@");
  if (start < 0 || (start > 0 && !/\s/.test(before[start - 1] ?? ""))) return null;
  const query = before.slice(start + 1);
  if (query.length > 40 || /^\s|\n|\s\s/.test(query)) return null;
  return { start, query };
}

/** People to offer for a mention: names (or a word of them) starting with the query, never yourself. */
export function mentionCandidates(people: readonly ChatUser[], query: string, selfId: string, limit = 6): ChatUser[] {
  const q = query.toLocaleLowerCase();
  const scored = people.flatMap((person) => {
    if (person.id === selfId) return [];
    const name = person.name.toLocaleLowerCase();
    if (name.startsWith(q)) return [{ person, rank: 0 }];
    if (name.split(/\s+/).some((word) => word.startsWith(q))) return [{ person, rank: 1 }];
    return [];
  });
  scored.sort((a, b) => a.rank - b.rank || a.person.name.localeCompare(b.person.name));
  return scored.slice(0, limit).map((s) => s.person);
}

/** Replaces the `@query` before the caret with `@Name ` and returns the new text and caret. */
export function insertMention(
  text: string,
  query: MentionQuery,
  caret: number,
  person: ChatUser,
): { text: string; caret: number } {
  const inserted = `@${person.name} `;
  return { text: text.slice(0, query.start) + inserted + text.slice(caret), caret: query.start + inserted.length };
}

export interface MentionMatch {
  start: number;
  end: number;
  userId: string;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Where `@Name` of each person appears in `text` (not inside a word, so not
 * in an email address), longest names first so "@Ann Lee" is not read as "@Ann".
 */
export function findMentions(text: string, people: readonly ChatUser[]): MentionMatch[] {
  const taken: MentionMatch[] = [];
  const byLength = [...people].filter((p) => p.name.trim() !== "").sort((a, b) => b.name.length - a.name.length);
  for (const person of byLength) {
    const pattern = new RegExp(`(?<![\\p{L}\\p{N}])@${escapeRegExp(person.name)}(?![\\p{L}\\p{N}])`, "giu");
    for (const match of text.matchAll(pattern)) {
      const start = match.index;
      const end = start + match[0].length;
      if (taken.some((m) => start < m.end && end > m.start)) continue;
      taken.push({ start, end, userId: person.id });
    }
  }
  return taken.sort((a, b) => a.start - b.start);
}

/**
 * The people a draft mentions (sent as `mentions`): participants named as
 * `@Name`, never yourself. When several share a name, only those picked from
 * the suggestions (`picked`) count, since the text alone can't tell them apart.
 */
export function mentionedIds(
  text: string,
  people: readonly ChatUser[],
  selfId: string,
  picked: ReadonlySet<string> = new Set(),
): string[] {
  const byName = new Map<string, ChatUser[]>();
  for (const person of people) {
    const key = person.name.toLocaleLowerCase();
    byName.set(key, [...(byName.get(key) ?? []), person]);
  }
  // One stand-in per name (its id is the name key), expanded to the people it stands for.
  const chosen = new Map<string, string[]>();
  const standIns: ChatUser[] = [];
  for (const [key, group] of byName) {
    const ids = (group.length === 1 ? group : group.filter((p) => picked.has(p.id))).map((p) => p.id);
    if (ids.length === 0) continue;
    chosen.set(key, ids);
    standIns.push({ id: key, name: group[0]!.name, avatar_url: null });
  }
  const ids = findMentions(text, standIns).flatMap((m) => chosen.get(m.userId) ?? []);
  return [...new Set(ids)].filter((id) => id !== selfId);
}

export type TextSegment = { text: string; mention: string | null };

/** A message body split around the `@Name`s of the people it mentions (yourself included), for highlighting. */
export function mentionSegments(message: Message, users: Users): TextSegment[] {
  const body = message.body ?? "";
  const people = message.mentions.flatMap((id) => (users[id] ? [users[id]] : []));
  const segments: TextSegment[] = [];
  let at = 0;
  for (const match of findMentions(body, people)) {
    if (match.start > at) segments.push({ text: body.slice(at, match.start), mention: null });
    segments.push({ text: body.slice(match.start, match.end), mention: match.userId });
    at = match.end;
  }
  if (at < body.length || segments.length === 0) segments.push({ text: body.slice(at), mention: null });
  return segments;
}

// ---------------------------------------------------------------- typing

/** Send `typing` at most this often while typing (the server drops more). */
export const TYPING_SEND_MS = 3_000;
/** Show someone as typing this long after their last `chat_typing`. */
export const TYPING_SHOW_MS = 6_000;

/** Who is typing where: conversation id → user id → shown until (ms). */
export type Typing = Readonly<Record<string, Readonly<Record<string, number>>>>;

export function withTyping(typing: Typing, conversationId: string, userId: string, untilMs: number): Typing {
  return { ...typing, [conversationId]: { ...typing[conversationId], [userId]: untilMs } };
}

/** Their message arrived (or they stopped): no longer typing. Returns `typing` when unchanged. */
export function withoutTyping(typing: Typing, conversationId: string, userId: string): Typing {
  const people = typing[conversationId];
  if (!people || !(userId in people)) return typing;
  const { [userId]: _, ...rest } = people;
  if (Object.keys(rest).length > 0) return { ...typing, [conversationId]: rest };
  const { [conversationId]: __, ...others } = typing;
  return others;
}

/** Drops entries shown until `nowMs` or earlier; returns `typing` when none expired. */
export function pruneTyping(typing: Typing, nowMs: number): Typing {
  let next = typing;
  for (const [conversationId, people] of Object.entries(typing)) {
    for (const [userId, until] of Object.entries(people)) {
      if (until <= nowMs) next = withoutTyping(next, conversationId, userId);
    }
  }
  return next;
}

export function typingUsers(typing: Typing, conversationId: string, nowMs: number): string[] {
  return Object.entries(typing[conversationId] ?? {})
    .filter(([, until]) => until > nowMs)
    .map(([userId]) => userId)
    .sort();
}

/** "Alice is typing…", "Alice and Bob are typing…", "Several people are typing…" ("Typing…" in a DM). */
export function typingText(userIds: readonly string[], users: Users, direct: boolean): string {
  if (userIds.length === 0) return "";
  if (direct) return "Typing…";
  const names = userIds.map((id) => personName(users, id));
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return "Several people are typing…";
}

// ---------------------------------------------------------------- settings

/** "Until I turn it back on" is stored as a far-future time. */
export const MUTE_FOREVER = "9999-12-31T00:00:00.000Z";
/** Mutes ending further away than this read as "until turned back on". */
const FOREVER_AFTER_MS = 5 * 365 * 86_400_000;

export const MUTE_CHOICES: readonly { label: string; ms: number | null }[] = [
  { label: "1 hour", ms: 3_600_000 },
  { label: "8 hours", ms: 8 * 3_600_000 },
  { label: "1 day", ms: 86_400_000 },
  { label: "Until I turn it back on", ms: null },
];

/** `muted_until` for a mute of `ms` from now (null: until turned back on). */
export function muteUntil(ms: number | null, nowMs: number): string {
  return ms === null ? MUTE_FOREVER : new Date(nowMs + ms).toISOString();
}

/** "Muted until 18:00", "Muted until Tue 09:00", "Muted until you turn it back on"; null when not muted. */
export function muteText(conversation: Conversation, nowMs: number, locale?: string): string | null {
  if (!isMuted(conversation, nowMs) || conversation.muted_until === null) return null;
  const until = Date.parse(conversation.muted_until);
  if (until - nowMs > FOREVER_AFTER_MS) return "Muted until you turn it back on";
  const time = timeText(until, locale);
  if (dayKey(new Date(until)) === dayKey(new Date(nowMs))) return `Muted until ${time}`;
  return `Muted until ${new Date(until).toLocaleDateString(locale, { weekday: "short" })} ${time}`;
}

// ---------------------------------------------------- disappearing messages

/** Disappearing-message timers (seconds; null: off), as the settings offer them. */
export const TIMER_CHOICES: readonly { value: number; label: string }[] = [
  { value: 86_400, label: "1 day" },
  { value: 604_800, label: "7 days" },
  { value: 7_776_000, label: "90 days" },
];

/** "1 day", "7 days", "90 days" (any other length in whole days, or hours under a day). */
export function timerLabel(seconds: number): string {
  const choice = TIMER_CHOICES.find((item) => item.value === seconds);
  if (choice) return choice.label;
  if (seconds < 86_400) {
    const hours = Math.max(1, Math.round(seconds / 3_600));
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  const days = Math.round(seconds / 86_400);
  return `${days} ${days === 1 ? "day" : "days"}`;
}

/** Whether a disappearing message is gone by now (the client hides it before the server's sweep). */
export function isExpired(message: Pick<Message, "expires_at">, nowMs: number): boolean {
  return !!message.expires_at && Date.parse(message.expires_at) <= nowMs;
}

/**
 * Who may change a conversation's timer: either person in a writable DM,
 * an admin in a circle or event chat that is still writable.
 */
export function canSetTimer(conversation: Conversation, admin: boolean): boolean {
  return conversation.writable && (conversation.kind === "direct" || admin);
}

export const NOTIFY_CHOICES: readonly { value: Conversation["notify"]; label: string }[] = [
  { value: "all", label: "All messages" },
  { value: "mentions", label: "Mentions and replies to you" },
  { value: "none", label: "Nothing" },
];

// ------------------------------------------------------------ safety, terms

export const REPORT_REASONS: readonly { value: ReportReason; label: string }[] = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate", label: "Hate" },
  { value: "violence", label: "Violence or threats" },
  { value: "sexual", label: "Sexual content" },
  { value: "self_harm", label: "Self-harm" },
  { value: "illegal", label: "Something illegal" },
  { value: "other", label: "Something else" },
];

/** Messages from people you blocked are collapsed behind "Show" (never your own). */
export function isCollapsed(message: Message, blocked: ReadonlySet<string>, shown: ReadonlySet<string>): boolean {
  return (
    message.kind === "text" && message.sender_id !== null && blocked.has(message.sender_id) && !shown.has(message.id)
  );
}

/** Whether the user must accept the server's acceptable-use terms before sending. */
export function needsTerms(termsVersion: number | undefined, accepted: number | null | undefined): boolean {
  return termsVersion !== undefined && (accepted ?? 0) < termsVersion;
}
